/**
 * Returns a function, that, when invoked, will only be triggered at most once during
 * a given window of time.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Time window (in milliseconds).
 * @returns {Function}
 */
function throttle(fn, wait = 300) {
  let throttleTimeoutId = -1;
  let tick = false;

  return () => {
    clearTimeout(throttleTimeoutId);
    throttleTimeoutId = setTimeout(fn, wait);

    if (!tick) {
      fn.call();
      tick = true;
      setTimeout(() => {
        tick = false;
      }, wait);
    }
  };
}

if (!customElements.get('sticky-atc')) {
  customElements.whenDefined('product-form').then(() => {
    class StickyAtc extends HTMLElement {
      constructor() {
        super();

        this.section = this.closest('.section-main-product');
        this.mainProductForm = this.section.querySelector('.js-product');
        this.buyButtons = this.section.querySelector('buy-buttons');
        this.imageContainer = this.querySelector('.sticky-atc__image');
        this.priceContainer = this.querySelector('.sticky-atc__price');
        this.buttonsWrapper = this.querySelector('.sticky-atc__buttons-wrapper');

        this.throttledOnScroll = throttle(StickyAtc.handleScroll.bind(this));
        window.addEventListener('scroll', this.throttledOnScroll);

        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
        this.section.addEventListener('on:media-gallery:change', this.updateImage.bind(this));

        // Wait for everything to load then clone buttons
        setTimeout(() => {
          this.updateImage();
          this.updatePrice();
          this.cloneButtonsFromMain();
        }, 500);
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.throttledOnScroll);
      }

      /**
       * Clone both buttons from main buy-buttons
       */
      cloneButtonsFromMain() {
        if (!this.buyButtons || !this.buttonsWrapper) return;

        const mainForm = this.buyButtons.querySelector('.js-product-form');
        if (!mainForm) return;

        const quantityRow = mainForm.querySelector('.quantity-submit-row');
        if (!quantityRow) return;

        // Find both buttons
        const addToCartBtn = quantityRow.querySelector('.add-to-cart');
        const paymentButtonContainer = quantityRow.querySelector('.shopify-payment-button');

        if (!addToCartBtn) return;

        // Create wrapper for buttons
        const buttonsContainer = document.createElement('div');
        buttonsContainer.className = 'sticky-atc__buttons';

        // Clone Add to Cart button with wrapper
        const atcWrapper = document.createElement('div');
        atcWrapper.className = 'sticky-atc__atc-wrapper';
        const clonedAtcBtn = addToCartBtn.cloneNode(true);
        atcWrapper.appendChild(clonedAtcBtn);
        buttonsContainer.appendChild(atcWrapper);

        // Clone Payment Button if it exists
        if (paymentButtonContainer) {
          const paymentWrapper = document.createElement('div');
          paymentWrapper.className = 'sticky-atc__payment-wrapper';
          
          // Deep clone the entire payment button structure
          const clonedPaymentBtn = paymentButtonContainer.cloneNode(true);
          paymentWrapper.appendChild(clonedPaymentBtn);
          buttonsContainer.appendChild(paymentWrapper);
        }

        // Clear and add to wrapper
        this.buttonsWrapper.innerHTML = '';
        this.buttonsWrapper.appendChild(buttonsContainer);

        // Store references
        this.addToCartBtn = this.buttonsWrapper.querySelector('.add-to-cart');
        
        // Add click handler to cloned Add to Cart button
        if (this.addToCartBtn) {
          this.addToCartBtn.addEventListener('click', (e) => {
            e.preventDefault();
            // Trigger the original button click
            const originalBtn = this.buyButtons.querySelector('.add-to-cart');
            if (originalBtn) {
              originalBtn.click();
            }
          });
        }

        // Re-initialize Shopify payment buttons
        setTimeout(() => {
          const paymentButton = this.buttonsWrapper.querySelector('shopify-accelerated-checkout');
          if (paymentButton) {
            // Force re-render by removing and re-adding
            const parent = paymentButton.parentNode;
            const clone = paymentButton.cloneNode(true);
            parent.removeChild(paymentButton);
            parent.appendChild(clone);
          }
        }, 100);
      }

      /**
       * Determine visibility after scroll.
       */
      static handleScroll() {
        const topOffset = document.querySelector('.pageheader--sticky') ? document.querySelector('.section-header').offsetHeight : 0;
        const isOut = this.buyButtons.getBoundingClientRect().bottom > topOffset;
        
        this.classList.toggle('sticky-atc--out', isOut);

        document.body.classList.toggle(
          'scrolled-to-bottom',
          window.scrollY + window.innerHeight + 100 > document.body.scrollHeight
        );
      }

      /**
       * Handle a change in variant on the page.
       * @param {Event} evt - Variant change event dispatched by variant-picker.
       */
      onVariantChange(evt) {
        this.updateImage();
        this.updatePrice();
        this.updateAddToCartButton(evt.detail.variant);
      }

      /**
       * Updates the image.
       */
      updateImage() {
        const mainImage = this.section.querySelector('.media-gallery .main-image .slider__item.is-active .product-media img');

        if (mainImage) {
          this.imageContainer.innerHTML = mainImage.outerHTML;
        } else {
          this.imageContainer.textContent = '';
        }
      }

      /**
       * Updates the price display.
       */
      updatePrice() {
        const mainPrice = this.section.querySelector('.product-info__price');
        
        if (mainPrice && this.priceContainer) {
          this.priceContainer.innerHTML = mainPrice.innerHTML;
        }
      }

      /**
       * Updates the "Add to Cart" button label and status.
       * @param {object} variant - Current variant object.
       */
      updateAddToCartButton(variant) {
        if (!this.addToCartBtn) return;

        const mainBtn = this.buyButtons.querySelector('.add-to-cart');
        if (mainBtn) {
          this.addToCartBtn.textContent = mainBtn.textContent;
          this.addToCartBtn.disabled = mainBtn.disabled;
        }
      }
    }

    customElements.define('sticky-atc', StickyAtc);
  });
}